'use client';

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';

import { initEmbedBridge } from '@/context/embed-bridge/utils';
import { createClient } from '@/lib/supabase/client';

type EmbedBridgeValue = {
  authenticated: boolean;
  appContainer: boolean;
  ready: boolean;
};

const defaultValue: EmbedBridgeValue = {
  authenticated: false,
  appContainer: false,
  ready: false,
};

const EmbedBridgeContext = createContext<EmbedBridgeValue>(defaultValue);

export function EmbedBridgeProvider({ children }: PropsWithChildren) {
  const [value, setValue] = useState<EmbedBridgeValue>(defaultValue);
  const supabase = useMemo(() => createClient(), []);

  useEffect(() => {
    const cleanup = initEmbedBridge(supabase, {
      onAuthReady: ({ authenticated, appContainer }) =>
        setValue((current) => ({ ...current, authenticated, appContainer, ready: true })),
      onLoggedOut: (appContainer) =>
        setValue((current) => ({ ...current, authenticated: false, appContainer, ready: true })),
      onSessionUpdated: ({ appContainer }) =>
        setValue((current) => ({ ...current, appContainer, authenticated: true, ready: true })),
    });

    return cleanup;
  }, [supabase]);

  return <EmbedBridgeContext.Provider value={value}>{children}</EmbedBridgeContext.Provider>;
}

export function useEmbedBridge() {
  return useContext(EmbedBridgeContext);
}

export function EmbedBridge({ children }: PropsWithChildren) {
  return <EmbedBridgeProvider>{children}</EmbedBridgeProvider>;
}
